/* ═══════════════════════════════════════════════════════════════════════
   "Where is home?", asked once per page.

   crAIg has two audiences and they mean different things by home. A
   business that has signed in means their dashboard — the chats, the
   inbox, the money. Everyone else means the front door at `/`, which is
   the pitch, and which stays exactly where it is: letcraig.ai is how a
   plumber finds out this exists.

   So a "home" link on any of crAIg's OWN surfaces resolves against the
   session rather than being hardcoded. Signed in, it takes you to your
   dashboard; signed out, it takes you to the page that explains what
   this is. One link, two correct answers.

   Why it defaults to `/` while the answer is in flight: a link that
   quietly changes destination under the cursor is worse than one that
   points at the marketing page for 200ms, and the signed-out case is
   both the common one and the one that survives being wrong — a signed-in
   owner who lands on the pitch has a Dashboard button waiting in its
   header, whereas a signed-out visitor sent to /dashboard gets bounced
   to a sign-in form they didn't ask for.

   `enabled` exists for one caller: a real business's intake must never
   make this request. That page belongs to the practice, its visitors are
   the practice's customers, and asking our server who they are is both
   pointless and not ours to ask.
   ═══════════════════════════════════════════════════════════════════════ */

import { useEffect, useState } from "react";

/** null while unknown — callers that render differently should wait for it. */
export function useSignedIn(enabled = true): boolean | null {
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    fetch("/api/account/me")
      .then((r) => (r.ok ? r.json() : { signedIn: false }))
      .then((me: { signedIn?: boolean }) => {
        if (live) setSignedIn(Boolean(me.signedIn));
      })
      .catch(() => {
        // Offline, or the database is waking. Home is the front door.
        if (live) setSignedIn(false);
      });
    return () => {
      live = false;
    };
  }, [enabled]);

  return enabled ? signedIn : false;
}

/** Where "home" points for whoever is reading. */
export function useHomeHref(enabled = true): string {
  return useSignedIn(enabled) ? "/dashboard" : "/";
}
